import React, { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import type { SpaceType } from '@/pages/Settings/UserManagement/SpaceAssignments';

interface SpaceAccessGuardProps {
  children: React.ReactNode;
}

/**
 * SpaceAccessGuard - Checks URL parameters for space IDs and validates user access.
 * If the user doesn't have access to the space in the URL, redirects to home.
 * If user has no access at all, still renders the dashboard (with empty sidebar).
 */
export const SpaceAccessGuard: React.FC<SpaceAccessGuardProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, isLoading, isUnrestricted, getParentChain } = useSpaceAccess();

  // Extract space IDs from URL parameters
  const urlSpaces = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const spaces: { type: SpaceType; id: string }[] = [];

    // Check each URL parameter for space IDs
    const spaceId = params.get('spaceId'); // Floor ID (format: space_xxx or floor_xxx)
    const roomId = params.get('roomId');
    const zoneId = params.get('zoneId');
    const buildingId = params.get('buildingId');

    if (spaceId) spaces.push({ type: 'floor', id: spaceId });
    if (roomId) spaces.push({ type: 'room', id: roomId });
    if (zoneId) spaces.push({ type: 'zone', id: zoneId });
    if (buildingId) spaces.push({ type: 'building', id: buildingId });

    return spaces;
  }, [location.search]);

  // Check if user has access to the deepest (most specific) space in the URL.
  // Shallower params are navigational context, not access targets — e.g. a
  // user whose only grant is `room: PILOT_CLEAN_ROOM` navigating to
  // `/?spaceId=<floor>&roomId=<room>` should NOT be blocked on the floor
  // check just because their room lives on a floor they weren't explicitly
  // granted. Their room grant is sufficient.
  //
  // Priority (deepest first): room > zone > floor (spaceId) > building.
  //
  // When the deepest space is a room or zone, we additionally verify any
  // accompanying `spaceId` (floor) matches the leaf's actual parent floor.
  // Without this, a user with a room grant could craft
  // `?spaceId=<UNRELATED_FLOOR>&roomId=<GRANTED_ROOM>` — the Dashboard's
  // floor-scoped queries (`useDashboardFloorQuery`, `useMainboardSpaceQuery`)
  // fire on `spaceId` alone and would surface the unrelated floor's name,
  // building/site, capacity, sensor list, and room/zone lists. Backend
  // `floors(ids:)` / `rooms(ids:)` / `zones(ids:)` resolvers filter only at
  // org-level (`pkg/graph/data/{floors,rooms,zones}.go`), so within-org
  // crafted URLs would leak data without this check.
  const blockedSpace = useMemo(() => {
    // Still loading - don't block yet
    if (isLoading) {
      return null;
    }

    // User is admin/superuser - allow all
    if (isUnrestricted) {
      return null;
    }

    // No spaces in URL - nothing to block
    if (urlSpaces.length === 0) {
      return null;
    }

    const deepest =
      urlSpaces.find((s) => s.type === 'room') ??
      urlSpaces.find((s) => s.type === 'zone') ??
      urlSpaces.find((s) => s.type === 'floor') ??
      urlSpaces.find((s) => s.type === 'building');

    if (!deepest) return null;
    if (!hasAccess(deepest.type, deepest.id)) return deepest;

    // Validate every non-deepest URL param. It's OK for a param to be an
    // ancestor of the deepest leaf (navigation context — parent floor or
    // building); anything else must have independent access. Without
    // this, downstream queries like `useMainboardZones` (fires on
    // `spaceId && zoneId`) or `useMainboardBuilding` (fires on
    // `buildingId`) would leak data from unrelated spaces when a crafted
    // URL pairs a granted leaf with unrelated sibling ids. Fail closed on
    // unknown parent chains so the parent-match check can't be bypassed
    // via a still-hydrating hierarchy entry.
    //
    // `site` is intentionally absent — urlSpaces only collects floor,
    // room, zone, building from URL params (no siteId param exists).
    const deepestChain =
      deepest.type === 'room' || deepest.type === 'zone' || deepest.type === 'floor'
        ? getParentChain(deepest.id)
        : undefined;

    for (const s of urlSpaces) {
      if (s === deepest) continue;

      const isAncestor =
        (s.type === 'floor' && deepestChain?.floor === s.id) ||
        (s.type === 'building' && deepestChain?.building === s.id);
      if (isAncestor) continue;

      // For room/zone leaves paired with a spaceId we also reject an
      // unknown parent chain (see block-comment above) — this falls out
      // naturally because deepestChain is undefined → ancestor match
      // fails → hasAccess check runs, which fails for a leaf-only user.
      if (!hasAccess(s.type, s.id)) return s;
    }

    return null;
  }, [isLoading, isUnrestricted, urlSpaces, hasAccess, getParentChain]);

  // Redirect to home if user doesn't have access to a space in the URL
  useEffect(() => {
    if (blockedSpace && !isLoading) {
      navigate('/');
    }
  }, [blockedSpace, isLoading, navigate]);

  // Don't render children until access is verified to prevent flash of protected content
  if (isLoading) {
    return null;
  }

  if (blockedSpace) {
    return null;
  }

  return <>{children}</>;
};

export default SpaceAccessGuard;
